
import { AdvancedOptions, IBook, IProject } from "../services/ttsService";
export type { AdvancedOptions, IBook, IProject };

export interface ModelOption { value: string; label: string; }

export interface Clip {
  id:        string;
  name:      string;
  chapter:   string;
  blobUrl:   string;
  base64:    string;
  selected:  boolean;
  chars:     number;
  order?:number;
  savedToDb: boolean;
  scriptText?: string;
  createdAt?: string;
  
}


const clipCreatedTime = (clip: Clip): number =>
  new Date(clip.createdAt || 0).getTime();

export const sortClipsByCreatedAt = (clips: Clip[]): Clip[] =>
  [...clips].sort((a, b) => clipCreatedTime(a) - clipCreatedTime(b));


export const sortClipsForDisplay = (clips: Clip[]): Clip[] =>
  [...clips].sort((a, b) =>
    (a.order ?? Infinity) - (b.order ?? Infinity) || clipCreatedTime(a) - clipCreatedTime(b)
  );
export interface ChapterOrderEntry {
  name:  string;
  order: number;
}

export const sortChaptersByOrder = (entries: ChapterOrderEntry[]): ChapterOrderEntry[] =>
  [...entries].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));

/** Use persisted chapter order when available; otherwise fall back to clip creation time. */
export const orderedChapterNames = (
  chapterOrder: ChapterOrderEntry[],
  clips: Clip[] = [],
  chaptersWithoutClips: string[] = [],
): string[] => {
  if (chapterOrder.length) {
    return sortChaptersByOrder(chapterOrder).map((entry) => entry.name);
  }
  return chapterOrderFromClips(clips, chaptersWithoutClips);
};

export const getChapterSerial = (chapterOrder: ChapterOrderEntry[], chapterName: string): number =>
  sortChaptersByOrder(chapterOrder).find((entry) => entry.name === chapterName)?.order ?? 0;

/** Chapter order by earliest clip creation time, newest chapters first; empty chapters follow the same recency order. */
export const chapterOrderFromClips = (clips: Clip[], chaptersWithoutClips: string[] = []): string[] => {
  const earliestByChapter = new Map<string, number>();
  for (const clip of clips) {
    const t = clipCreatedTime(clip);
    const prev = earliestByChapter.get(clip.chapter);
    if (prev === undefined || t < prev) earliestByChapter.set(clip.chapter, t);
  }

  const fromClips = Array.from(earliestByChapter.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([chapter]) => chapter);

  const emptyChapters = chaptersWithoutClips
    .filter((ch) => !earliestByChapter.has(ch))
    .reverse();

  return [...emptyChapters, ...fromClips];
};

export interface MergedChapterUI {
  id:          string;
  chapterName: string;
  blobUrl:     string;  
  clipCount:   number;
  totalChars:  number;
  createdAt:   string;
}

export interface BookConfig {
  title:             string;
  author:            string;
  language:          string;
  model:             string;
  platform:          string;
  realVoice:         string;
  voiceLabel:        string;
  styleInstructions: string;
  notes?:            string;
  advanced:          AdvancedOptions;

}

export const DEFAULT_ADVANCED: AdvancedOptions = {
  speakingRate:     1.0,
  pitch:            0,
  volumeGainDb:     0,
  temperature:      0.01,
  topP:             1,
  topK:             1,
  stability:        50,
  similarityBoost:  75,
  style:            0,
  useSpeakerBoost:  true,
  audioEncoding:    "LINEAR16",
  sampleRateHertz:  44100,
  effectsProfileId: "",
};

/** ElevenLabs v3 defaults: stability 0.82, similarity_boost 0.78, style 0 (UI 0–100 scale). */
export const DEFAULT_ELEVEN_ADVANCED: AdvancedOptions = {
  ...DEFAULT_ADVANCED,
  stability:        82,
  similarityBoost:  78,
  style:            0,
  useSpeakerBoost:  true,
};

/** Ensures sampling + audio keys exist (older API/DB rows may omit temperature/topP/topK). */
export function mergeAdvanced(partial?: Partial<AdvancedOptions> | null): AdvancedOptions {
  return { ...DEFAULT_ADVANCED, ...(partial ?? {}) };
}

export function mergeElevenAdvanced(partial?: Partial<AdvancedOptions> | null): AdvancedOptions {
  return { ...DEFAULT_ELEVEN_ADVANCED, ...(partial ?? {}) };
}

/** Default book language when platform is ElevenLabs (new book modal). */
export const DEFAULT_ELEVEN_LANGUAGE = "lv-LV";

export const DEFAULT_BOOK_CONFIG: BookConfig = {
  title:             "",
  author:            "",
  language:          DEFAULT_ELEVEN_LANGUAGE,
  model:             "",
  platform:          "ElevenLabs",
  realVoice:         "",
  voiceLabel:        "",
  styleInstructions: "",
  advanced:          { ...DEFAULT_ELEVEN_ADVANCED },
};

